import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Calendar, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { newsItems as fallbackNews } from '../mock';
import { fetchNews, resolveImg } from '../api';

const NewsSection = () => {
  const [newsItems, setNewsItems] = useState(fallbackNews);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  useEffect(() => {
    fetchNews().then((data) => { if (data && data.length) setNewsItems(data.slice(0, 3)); }).catch(() => {});
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };

  return (
    <section className="relative py-32 bg-black overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-fuchsia-950/5 to-black" />
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-fuchsia-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="inline-block px-4 py-2 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-full text-fuchsia-300 text-sm font-medium tracking-wider mb-6">
            Actualités
          </span>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Dernières <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Nouvelles</span>
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Restez informés de toutes les actualités, événements et succès de notre club
          </p>
        </motion.div>

        {/* News Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {newsItems.map((item, index) => {
            const label = item.linkLabel || 'Lire la suite';
            const to = item.link || `/actualites#news-${item.id}`;
            const isExternal = /^https?:\/\//i.test(to);
            return (
            <motion.article
              key={item.id}
              data-testid={`home-news-card-${index}`}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              className="group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-fuchsia-500/50 transition-all duration-500"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <motion.img
                  src={resolveImg(item.image)}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-fuchsia-500/90 backdrop-blur-sm rounded-full text-white text-xs font-medium">
                    {item.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 text-white/50 text-sm mb-3">
                  <Calendar size={14} />
                  <time>{new Date(item.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</time>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-fuchsia-400 transition-colors duration-300">
                  {item.title}
                </h3>
                
                <p className="text-white/60 text-sm leading-relaxed mb-4">
                  {item.excerpt}
                </p>

                {isExternal ? (
                  <a
                    href={to}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`home-news-cta-${index}`}
                    className="inline-flex items-center gap-2 text-fuchsia-400 text-sm font-medium hover:gap-3 transition-all duration-300"
                  >
                    {label}
                    <ArrowRight size={16} />
                  </a>
                ) : (
                  <Link
                    to={to}
                    data-testid={`home-news-cta-${index}`}
                    className="inline-flex items-center gap-2 text-fuchsia-400 text-sm font-medium hover:gap-3 transition-all duration-300"
                  >
                    {label}
                    <ArrowRight size={16} />
                  </Link>
                )}
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 bg-gradient-to-t from-fuchsia-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            </motion.article>
          );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default NewsSection;
